import React from 'react';
import type { CheckedState } from '@radix-ui/react-checkbox';
import { Checkbox as CheckboxPrimitive } from '../../shadcn/ui/checkbox';
import { cn } from '../../lib/cn';

export interface CheckboxProps {
  /** Whether the checkbox is checked */
  checked: boolean;
  /** Whether the checkbox is in indeterminate state (for "select all" partial selection) */
  indeterminate?: boolean;
  /** Change handler */
  onChange: (checked: boolean) => void;
  /** Whether the checkbox is disabled */
  disabled?: boolean;
  /** Optional label text */
  label?: string;
  /** Optional aria-label for accessibility */
  ariaLabel?: string;
  /** Optional class name */
  className?: string;
}

/**
 * Gmail-style checkbox with checked, unchecked, and indeterminate states.
 * Used for document selection in the feed.
 *
 * Rebuilt on the DS-internal shadcn `ui/checkbox` primitive (Radix) with this
 * public API kept byte-for-byte (0 frontend imports; 4 DS-internal consumers:
 * LoginForm, SelectionBar, Document, IntegrationCard). Radix natively supports
 * `checked="indeterminate"` and emits `role="checkbox"` + `aria-checked="mixed"`,
 * so the box/indicator/keyboard semantics come from the primitive.
 *
 * The wrapper `<label>` still stops click propagation so a checkbox click never
 * bubbles to an ancestor feed-row's `onClick` (the double-toggle guard). The
 * legacy molecule ALSO `preventDefault()`-ed to suppress a native label→input
 * double-toggle; the Radix `<button>` has no native label/input pairing, so only
 * `stopPropagation` remains necessary.
 */
export const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  indeterminate = false,
  onChange,
  disabled = false,
  label,
  ariaLabel,
  className = '',
}) => {
  // Stop the click bubbling to an ancestor row's onClick (feed-card click
  // protection). Radix owns the toggle on the button itself.
  const stopBubble = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleCheckedChange = (next: CheckedState) => {
    if (disabled) return;
    // A user toggle from the checked/indeterminate box always resolves to a
    // boolean; coerce defensively (matches the legacy onChange(!checked)).
    onChange(next === true);
  };

  return (
    <label
      className={cn(
        'tw:inline-flex tw:items-center tw:gap-2 tw:select-none',
        disabled ? 'tw:cursor-not-allowed' : 'tw:cursor-pointer',
        className
      )}
      onClick={stopBubble}
    >
      <CheckboxPrimitive
        checked={indeterminate ? 'indeterminate' : checked}
        onCheckedChange={handleCheckedChange}
        disabled={disabled}
        aria-label={ariaLabel || label}
      />
      {label && (
        <span
          className={cn(
            'tw:text-sm',
            disabled ? 'tw:text-muted-foreground' : 'tw:text-foreground'
          )}
        >
          {label}
        </span>
      )}
    </label>
  );
};

export default Checkbox;
